import React, { useState, useEffect, useRef } from 'react';
import { ChevronUp, Mail, HelpCircle } from 'lucide-react';
import '../styles/PullableFooter.css';

const PullableFooter = ({ onNavigate }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [isVisible, setIsVisible] = useState(false);
  const [isClosing, setIsClosing] = useState(false);
  const [dragOffset, setDragOffset] = useState(0);
  const [maxDragHeight, setMaxDragHeight] = useState(200);
  const scrollTimeoutRef = useRef(null);
  const footerRef = useRef(null);
  const dragStartY = useRef(null);
  const isDragging = useRef(false);
  const currentDragOffset = useRef(0);

  // Measure footer height when it opens
  useEffect(() => {
    if (isOpen && footerRef.current) {
      const footerHeight = footerRef.current.offsetHeight;
      setMaxDragHeight(footerHeight);
    }
  }, [isOpen]);

  useEffect(() => {
    const handleScroll = () => {
      // Show footer tab when scrolled down at least 300px
      const shouldShow = window.scrollY > 300;

      if (shouldShow) {
        setIsVisible(true);

        // Clear existing timeout
        if (scrollTimeoutRef.current) {
          clearTimeout(scrollTimeoutRef.current);
        }

        // Set new timeout to hide footer 3 seconds after scrolling stops
        scrollTimeoutRef.current = setTimeout(() => {
          setIsVisible(false);
          setIsOpen(false);
        }, 3000);
      } else {
        setIsVisible(false);
        setIsOpen(false);
      }
    };

    window.addEventListener('scroll', handleScroll);

    return () => {
      window.removeEventListener('scroll', handleScroll);
      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current);
      }
    };
  }, []);

  // Click outside to close
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (isOpen && footerRef.current && !footerRef.current.contains(event.target)) {
        closeFooter();
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  const closeFooter = () => {
    setIsClosing(true);
    setTimeout(() => {
      setIsOpen(false);
      setIsClosing(false);
    }, 300);
  };

  const toggleFooter = () => {
    if (!isDragging.current) {
      if (isOpen) {
        closeFooter();
      } else {
        setIsOpen(true);
      }
      // Clear timeout when manually toggling
      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current);
      }
      // Restart 3-second timer
      scrollTimeoutRef.current = setTimeout(() => {
        setIsVisible(false);
        setIsOpen(false);
      }, 3000);
    }
  };

  // Drag handlers
  const handleDragStart = (e) => {
    const clientY = e.type.includes('touch') ? e.touches[0].clientY : e.clientY;
    dragStartY.current = clientY;
    currentDragOffset.current = 0;
    isDragging.current = false;

    // Add global listeners for drag
    if (e.type.includes('touch')) {
      document.addEventListener('touchmove', handleDragMove);
      document.addEventListener('touchend', handleDragEnd);
    } else {
      document.addEventListener('mousemove', handleDragMove);
      document.addEventListener('mouseup', handleDragEnd);
    }
  };

  const handleDragMove = (e) => {
    if (dragStartY.current === null) return;

    const clientY = e.type.includes('touch') ? e.touches[0].clientY : e.clientY;
    const deltaY = dragStartY.current - clientY;

    // If moved more than 5px, consider it a drag
    if (Math.abs(deltaY) > 5) {
      isDragging.current = true;

      // Apply elastic resistance when pulling beyond 80% of max height
      let effectiveDelta = deltaY;
      const elasticThreshold = maxDragHeight * 0.8;

      if (Math.abs(deltaY) > elasticThreshold) {
        const excess = Math.abs(deltaY) - elasticThreshold;
        const resistance = 0.3; // 30% resistance
        const resistedExcess = excess * resistance;
        effectiveDelta = (deltaY > 0 ? 1 : -1) * (elasticThreshold + resistedExcess);
      }

      // Hard limit at maxDragHeight
      const clampedDelta = Math.max(-maxDragHeight, Math.min(maxDragHeight, effectiveDelta));
      currentDragOffset.current = clampedDelta;
      setDragOffset(clampedDelta);
    }
  };

  const handleDragEnd = (e) => {
    if (dragStartY.current === null) return;

    // Remove global listeners
    document.removeEventListener('mousemove', handleDragMove);
    document.removeEventListener('mouseup', handleDragEnd);
    document.removeEventListener('touchmove', handleDragMove);
    document.removeEventListener('touchend', handleDragEnd);

    const clientY = e.type.includes('touch') ? e.changedTouches[0].clientY : e.clientY;
    const deltaY = dragStartY.current - clientY;

    // Reset drag offset
    setDragOffset(0);
    currentDragOffset.current = 0;

    // Dragged up more than 50px = open
    if (deltaY > 50 && !isOpen) {
      setIsOpen(true);
      // Keep visible and reset timer
      setIsVisible(true);
      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current);
      }
      scrollTimeoutRef.current = setTimeout(() => {
        setIsVisible(false);
        setIsOpen(false);
      }, 3000);
    }
    // Dragged down more than 50px = close
    else if (deltaY < -50 && isOpen) {
      closeFooter();
    }

    dragStartY.current = null;
    setTimeout(() => {
      isDragging.current = false;
    }, 100);
  };

  const handleFAQClick = (e) => {
    e.preventDefault();
    if (onNavigate) {
      onNavigate('faq');
      setIsOpen(false);
    }
  };

  // Calculate dynamic transform based on drag
  const getFooterStyle = () => {
    if (isDragging.current && dragOffset !== 0) {
      // During drag, show footer pulled up by drag amount
      return {
        transform: `translateX(-50%) translateY(calc(100% - ${dragOffset}px))`,
        transition: 'none',
        opacity: 1
      };
    }

    if (!isVisible) {
      return {
        transform: 'translateX(-50%) translateY(100%)',
        opacity: 0
      };
    }

    return {};
  };

  return (
    <>
      {/* Invisible drag trigger area at bottom of screen */}
      <div
        className="footer-drag-trigger"
        onMouseDown={handleDragStart}
        onTouchStart={handleDragStart}
      />

      <div
        ref={footerRef}
        className={`pullable-footer ${isVisible ? 'showing' : 'hiding'}`}
        style={getFooterStyle()}
      >
        <div className="footer-container">
          {/* Footer Content - Show during drag or when open */}
          {(isOpen || (isDragging.current && dragOffset > 20)) && (
            <div
              className={`footer-content ${isClosing ? 'closing' : ''} ${isDragging.current ? 'dragging' : ''}`}
              style={{
                opacity: isDragging.current && !isOpen ? Math.min(1, dragOffset / 100) : 1
              }}
            >
              <div className="footer-info">
                <div className="footer-section">
                  <strong>MobiDeal</strong> by Herbrax
                </div>
                <div className="footer-section">
                  © 2025 MobiDeal
                </div>
                <div className="footer-section footer-links">
                  <a href="mailto:admin@mobideal.ca" className="footer-link">
                    <Mail size={14} />
                    admin@mobideal.ca
                  </a>
                  <button onClick={handleFAQClick} className="footer-link footer-link-button">
                    <HelpCircle size={14} />
                    FAQ
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Pull Tab */}
          <button
            className="footer-tab"
            onClick={toggleFooter}
            onMouseDown={handleDragStart}
            onTouchStart={handleDragStart}
          >
            <ChevronUp className={`tab-icon ${isOpen ? 'rotated' : ''}`} />
          </button>
        </div>
      </div>
    </>
  );
};

export default PullableFooter;
